import React, { useState, useEffect } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { getInquiries, updateInquiryStatus } from '../admin-firebase';
import app from '../firebase';

const AdminDashboard = ({ user, onLogout }) => {
    const [inquiries, setInquiries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedInquiry, setSelectedInquiry] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        loadInquiries();
    }, [selectedStatus]);

    const loadInquiries = async () => {
        setIsLoading(true);
        setError('');

        try {
            const params = {};
            if (selectedStatus !== 'all') {
                params.status = selectedStatus;
            }

            const result = await getInquiries(params);
            setInquiries(result.inquiries);
        } catch (error) {
            console.error('Error loading inquiries:', error);
            setError('문의 목록을 불러오는 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusUpdate = async (inquiryId, newStatus, adminNote = '') => {
        setIsUpdating(true);
        setError('');

        try {
            await updateInquiryStatus({
                inquiryId,
                status: newStatus,
                adminNote
            });

            // 목록 새로고침
            await loadInquiries();
            setSelectedInquiry(null);
        } catch (error) {
            console.error('Error updating status:', error);
            setError('상태 업데이트 중 오류가 발생했습니다.');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleLogout = async () => {
        try {
            const auth = getAuth(app);
            await signOut(auth);
            onLogout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { text: '대기중', color: '#ffc107', bgColor: '#fff3cd' },
            'in-progress': { text: '처리중', color: '#17a2b8', bgColor: '#d1ecf1' },
            completed: { text: '완료', color: '#28a745', bgColor: '#d4edda' }
        };

        const config = statusConfig[status] || statusConfig.pending;
        return (
            <span style={{
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '0.8rem',
                color: config.color,
                backgroundColor: config.bgColor,
                border: `1px solid ${config.color}`
            }}>
                {config.text}
            </span>
        );
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString('ko-KR');
    };

    return (
        <div className="container">
            <div className="header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1>관리자 대시보드</h1>
                        <p>TOBE RICH 문의 관리</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                            {user.email}
                        </span>
                        <button
                            onClick={handleLogout}
                            className="btn"
                            style={{ backgroundColor: '#6c757d' }}
                        >
                            로그아웃
                        </button>
                    </div>
                </div>
            </div>

            <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2>문의 목록</h2>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        >
                            <option value="all">전체</option>
                            <option value="pending">대기중</option>
                            <option value="in-progress">처리중</option>
                            <option value="completed">완료</option>
                        </select>
                        <button
                            onClick={loadInquiries}
                            className="btn"
                            style={{ backgroundColor: '#17a2b8' }}
                            disabled={isLoading}
                        >
                            새로고침
                        </button>
                    </div>
                </div>

                {error && (
                    <div style={{
                        marginBottom: '16px',
                        padding: '12px',
                        backgroundColor: '#f8d7da',
                        color: '#721c24',
                        borderRadius: '4px',
                        fontSize: '0.9rem'
                    }}>
                        ❌ {error}
                    </div>
                )}

                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        로딩 중...
                    </div>
                ) : inquiries.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                        문의가 없습니다.
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>이름</th>
                                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>이메일</th>
                                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>유형</th>
                                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>상태</th>
                                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>등록일</th>
                                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>작업</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inquiries.map((inquiry) => (
                                    <tr key={inquiry.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                                        <td style={{ padding: '12px' }}>{inquiry.name}</td>
                                        <td style={{ padding: '12px' }}>{inquiry.email}</td>
                                        <td style={{ padding: '12px' }}>{inquiry.category}</td>
                                        <td style={{ padding: '12px' }}>{getStatusBadge(inquiry.status)}</td>
                                        <td style={{ padding: '12px' }}>{formatDate(inquiry.createdAt)}</td>
                                        <td style={{ padding: '12px' }}>
                                            <button
                                                onClick={() => setSelectedInquiry(inquiry)}
                                                className="btn"
                                                style={{ fontSize: '0.8rem', padding: '4px 8px' }}
                                            >
                                                상세보기
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* 문의 상세 모달 */}
            {selectedInquiry && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '24px',
                        borderRadius: '8px',
                        maxWidth: '600px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflow: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3>문의 상세</h3>
                            <button
                                onClick={() => setSelectedInquiry(null)}
                                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <strong>이름:</strong> {selectedInquiry.name}
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <strong>이메일:</strong> {selectedInquiry.email}
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <strong>전화번호:</strong> {selectedInquiry.phone || '-'}
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <strong>유형:</strong> {selectedInquiry.category}
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <strong>상태:</strong> {getStatusBadge(selectedInquiry.status)}
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <strong>등록일:</strong> {formatDate(selectedInquiry.createdAt)}
                        </div>
                        <div style={{ marginBottom: '20px' }}>
                            <strong>문의 내용:</strong>
                            <div style={{
                                marginTop: '8px',
                                padding: '12px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {selectedInquiry.message}
                            </div>
                        </div>

                        {selectedInquiry.adminNote && (
                            <div style={{ marginBottom: '20px' }}>
                                <strong>관리자 메모:</strong>
                                <div style={{
                                    marginTop: '8px',
                                    padding: '12px',
                                    backgroundColor: '#fff3cd',
                                    borderRadius: '4px'
                                }}>
                                    {selectedInquiry.adminNote}
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => handleStatusUpdate(selectedInquiry.id, 'pending')}
                                className="btn"
                                style={{ backgroundColor: '#ffc107' }}
                                disabled={isUpdating || selectedInquiry.status === 'pending'}
                            >
                                대기중으로 변경
                            </button>
                            <button
                                onClick={() => handleStatusUpdate(selectedInquiry.id, 'in-progress')}
                                className="btn"
                                style={{ backgroundColor: '#17a2b8' }}
                                disabled={isUpdating || selectedInquiry.status === 'in-progress'}
                            >
                                처리중으로 변경
                            </button>
                            <button
                                onClick={() => handleStatusUpdate(selectedInquiry.id, 'completed')}
                                className="btn"
                                style={{ backgroundColor: '#28a745' }}
                                disabled={isUpdating || selectedInquiry.status === 'completed'}
                            >
                                완료로 변경
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard; 